import { FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { APP_CURRENCY, SUPPORTED_CURRENCIES, type AppCurrency } from '@financial/shared';
import { useAuthStore } from '../store/auth';
import { useRegister } from '../features/auth/useAuth';
import { getApiErrorMessage } from '../lib/errors';
import { Field } from '../components/ui/Field';
import { AuthShell } from './LoginPage';

const CURRENCY_LABELS: Record<AppCurrency, string> = {
  MXN: 'Pesos mexicanos (MXN)',
  USD: 'Dólares estadounidenses (USD)',
  CAD: 'Dólares canadienses (CAD)',
};

export function RegisterPage() {
  const isAuth = useAuthStore((s) => s.isAuthenticated());
  const navigate = useNavigate();
  const register = useRegister();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [currency, setCurrency] = useState<AppCurrency>(APP_CURRENCY);
  const passwordsDiffer = Boolean(passwordConfirm) && password !== passwordConfirm;

  if (isAuth) return <Navigate to="/panorama" replace />;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (passwordsDiffer || !privacyAccepted || !ageConfirmed) return;
    register.mutate(
      { name, email, password, privacyAccepted: true, ageConfirmed: true, currency },
      {
        onSuccess: () =>
          navigate(`/verificar-correo?email=${encodeURIComponent(email)}`, { replace: true }),
      },
    );
  };

  return (
    <AuthShell title="Crea tu cuenta" subtitle="Empieza con una fotografía clara de tu dinero hoy.">
      <form onSubmit={onSubmit} className="space-y-4">
        <Field
          id="name"
          label="Nombre"
          type="text"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Field
          id="email"
          label="Correo"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div>
          <label htmlFor="currency" className="mb-1 block text-sm font-medium text-slate-700">
            Moneda inicial
          </label>
          <select
            id="currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as AppCurrency)}
            className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
            aria-describedby="currency-help"
          >
            {SUPPORTED_CURRENCIES.map((supportedCurrency) => (
              <option key={supportedCurrency} value={supportedCurrency}>
                {CURRENCY_LABELS[supportedCurrency]}
              </option>
            ))}
          </select>
          <p id="currency-help" className="mt-1 text-xs leading-5 text-slate-500">
            Crearemos Efectivo en esta moneda y la usaremos para mostrar tus montos. No convierte
            importes. Podrás cambiar la moneda predeterminada en Ajustes, pero no la de una cuenta
            ya creada.
          </p>
        </div>
        <Field
          id="password"
          label="Contraseña"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          helperText="Usa al menos 8 caracteres."
        />
        <Field
          id="password-confirm"
          label="Confirmar contraseña"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          errorText={passwordsDiffer ? 'Las contraseñas no coinciden.' : undefined}
        />
        <label className="flex min-h-11 cursor-pointer items-start gap-3 py-1 text-xs leading-5 text-slate-600">
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300"
            required
          />
          <span>
            Leí y acepto el{' '}
            <Link
              to="/privacidad"
              target="_blank"
              rel="noreferrer"
              data-allow-small-target="true"
              className="font-semibold text-slate-900 underline"
            >
              aviso de privacidad
            </Link>
            .
          </span>
        </label>
        <label className="flex min-h-11 cursor-pointer items-start gap-3 py-1 text-xs leading-5 text-slate-600">
          <input
            type="checkbox"
            checked={ageConfirmed}
            onChange={(e) => setAgeConfirmed(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300"
            required
          />
          <span>Confirmo que soy mayor de edad.</span>
        </label>
        {register.isError && (
          <p role="alert" className="text-sm text-expense">
            {getApiErrorMessage(register.error)}
          </p>
        )}
        <button
          type="submit"
          disabled={register.isPending}
          className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
        >
          {register.isPending ? 'Preparando verificación…' : 'Crear cuenta gratis'}
        </button>
      </form>
      <p className="mt-4 text-center text-sm text-slate-600">
        ¿Ya tienes cuenta?{' '}
        <Link
          to="/login"
          className="tap-target inline-flex items-center font-semibold text-slate-900 underline"
        >
          Inicia sesión
        </Link>
      </p>
    </AuthShell>
  );
}
